import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';

import Layout from '../UI/introGameLayout/layout';
import ElementsTable from '../UI/elementsTable';

import {
  generalInfoSummaryQuery,
  consolidatedInsightsQuery,
  scenarioLevelsQuery,
} from './api';

import {
  popupMessagesSummaryDecisionBreakdown,
  summaryDecisionBreakdownElementsHeaders,
  summaryDecisionBreakdownAllOtherAccountsHeaders
} from './data';

import PopupMessages from '../UI/introGameLayout/popupMessages';

import './styles.css';

export default function ClassificationSummaryDecisionBreakdown() {
  const navigate = useNavigate();

  const {
    getUser,
    getCurrentClassificationGameId
  } = useAuth();

  const user = getUser();

  const gameId = getCurrentClassificationGameId();

  const [isFirstGameOfTheType, setIsFirstGameOfTheType] = useState(false);

  const [elementsTableData, setElementsTableData] = useState([]);
  const [allOtherAccountsTableData, setAllOtherAccountsTableData] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const { data: generalInfo } = useQuery(generalInfoSummaryQuery()) || {};
  const { data: consolidatedInsights } = useQuery({
    ...consolidatedInsightsQuery({ userId: user?.id }),
    enabled: !!user?.id,
  });

  const games = generalInfo?.games;
  const gamesSessions = generalInfo?.sessions;

  const game = games?.find(g => g.game_type.includes('Account Classification') && g.id === gameId);
  const scenarioId = game?.scenario;
  const { data: scenarioLevels } = useQuery(scenarioLevelsQuery({ scenarioId }));
  // Both classified-multistep and any realco level (name starts with
  // 'real-public-co-') use the same Classified Balance Sheet + Multi-
  // Step Income Statement gameplay, so either should route through the
  // multistep breakdown page.
  const hasClassifiedMultistep = !!scenarioLevels?.some(sl =>
    sl.name === 'classified-multistep'
    || sl.name?.startsWith?.('real-public-co')
  );

  useEffect(() => {
    if(gamesSessions && games && gameId && user) {
      if(gamesSessions.filter(
          session =>
            games.find(game => game.id === session.game && game.game_type === 'Account Classification')
        ).length < 2) {
        setIsFirstGameOfTheType(true);
      }
    }
  }, [gamesSessions, games, gameId, user]);

  useEffect(() => {
    if(consolidatedInsights) {
      const classificationInsights = consolidatedInsights.classification || [];

      const elementsData = [];
      const allOtherAccountsData = [];

      classificationInsights.forEach(insight => {
        const row = {
          question: insight.question,
        };

        (insight.options || []).forEach(option => {
          row[option.option] = {
            value: option.count_users,
            isParentMatch: option.is_correct,
            background: option.count_users
              ? option.is_correct
                ? '#D1E9D6'
                : '#FD9595'
              : '#FFFFFF',
          };
        });

        if (summaryDecisionBreakdownAllOtherAccountsHeaders.includes(row.question)) {
          elementsData.push(row);
        } else {
          allOtherAccountsData.push(row);
        }
      });

      setElementsTableData(elementsData);
      setAllOtherAccountsTableData(allOtherAccountsData);
      setDataLoaded(true);
    }
  }, [consolidatedInsights]);

  const handlePopupMessagesNextButtonClick = () => {
    // When the scenario includes the classified-multistep level, slot the
    // dedicated breakdown page in between this page and feedback/final
    // so the player sees their multistep placements before wrapping up.
    if (hasClassifiedMultistep) {
      navigate('/classification/summary/decision-breakdown/classified-multistep');
      return;
    }
    let redirectPath = '/classification/summary/final';
    if(isFirstGameOfTheType) {
      redirectPath = '/classification/summary/feedback';
    }
    navigate(redirectPath);
  }

  const handlePopupMessagesPrevButtonClick = () => {
    navigate('/classification/summary/breakdown');
  }

  return(
    <Layout
      backgroundColor="#FBEDD5"
      isBackgroundBlurred={false}
      isHeaderVisible={true}
      isFooterVisible={false}
    >
      {
        dataLoaded &&
          <div className="game-summary-container game-summary-decision-breakdown-container">
            <PopupMessages
              messages={popupMessagesSummaryDecisionBreakdown}
              messageSize="small"
              buttonText="Next"
              onButtonClick={handlePopupMessagesNextButtonClick}
              additionalButtonText="Back"
              onAdditionalButtonClick={handlePopupMessagesPrevButtonClick}
            />
            <div className="summary-decision-breakdown-tables">
              <div className="summary-decision-breakdown-table-container">
                <ElementsTable
                  title="The Elements"
                  tableColumnHeader="Sorting Items"
                  tableHeaders={summaryDecisionBreakdownElementsHeaders}
                  tableData={elementsTableData}
                />
              </div>

              {
                allOtherAccountsTableData.length > 0 &&
                  <div className="summary-decision-breakdown-table-container">
                    <ElementsTable
                      title="All other Accounts (incl. the Tricky Accounts)"
                      tableColumnHeader="Sorting Items"
                      tableHeaders={summaryDecisionBreakdownAllOtherAccountsHeaders}
                      tableData={allOtherAccountsTableData}
                    />
                  </div>
              }
            </div>
          </div>
      }
    </Layout>
  );
}
